// The in-page Copilot's shared state: whether the side panel is open, which
// conversation it shows (kept across pages and reloads), and the shortcut.
// Lives in the app layout so the panel survives every navigation.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isSuperAdmin } from "../../utils/permissions";

const CopilotContext = createContext(null);
const OPEN_KEY = "copilot_panel_open";
const CHAT_KEY = "copilot_panel_chat";

const read = (k) => { try { return localStorage.getItem(k); } catch (_) { return null; } };
const write = (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (_) { /* private mode */ } };

export const CopilotProvider = ({ children }) => {
  // Same audience as the full Copilot page.
  const allowed = isSuperAdmin();
  const [open, setOpenState] = useState(() => allowed && read(OPEN_KEY) === "true");
  const [chatId, setChatIdState] = useState(() => read(CHAT_KEY) || null);

  const setOpen = useCallback((v) => { setOpenState(v); write(OPEN_KEY, v ? "true" : "false"); }, []);
  const setChatId = useCallback((id) => { setChatIdState(id || null); write(CHAT_KEY, id || null); }, []);
  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  // Ctrl/Cmd+J opens or closes the panel from anywhere.
  useEffect(() => {
    if (!allowed) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setOpen(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [allowed, open, setOpen]);

  const value = useMemo(() => ({ allowed, open, setOpen, toggle, chatId, setChatId }), [allowed, open, setOpen, toggle, chatId, setChatId]);
  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
};

export const useCopilot = () => useContext(CopilotContext) || { allowed: false, open: false, setOpen: () => {}, toggle: () => {}, chatId: null, setChatId: () => {} };

/** Pages listen for this to reload what Copilot just changed. */
export const COPILOT_CHANGED = "copilot:changed";
